import React, { useEffect, useState, useRef } from 'react';
import {
  Card,
  Form,
  Typography,
  message,
  Tabs,
  Space,
  Button,
  Spin,
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { customerService, CreateCustomerData, CreateCustomerFamilyData, CreateCustomerAssetData, CreateCustomerContactData, CreateCustomerFinancialData, CustomerChangeRequest } from '../services/customers';
import { bankService, Bank } from '../services/banks';
import { staffService, Staff } from '../services/staffs';
import api from '../services/api';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import { OverviewTab } from '../components/CustomerForm/OverviewTab';
import { EngagementTab } from '../components/CustomerForm/EngagementTab';
import { BusinessTab } from '../components/CustomerForm/BusinessTab';
import { FamilyTab } from '../components/CustomerForm/FamilyTab';
import { AssetsTab } from '../components/CustomerForm/AssetsTab';
import { TimelineTab } from '../components/CustomerForm/TimelineTab';

const { Title } = Typography;
const { TabPane } = Tabs;

interface User {
  id: number;
  name: string;
  email: string;
}

export const CustomerFormPage: React.FC = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm<CreateCustomerData>();
  const { user } = useAuth();
  const canUpdate = hasPermission(user, 'personal.update');
  const canDelete = hasPermission(user, 'personal.delete');
  const [submitting, setSubmitting] = useState(false);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [staff, setStaff] = useState<User[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [spouses, setSpouses] = useState<CreateCustomerFamilyData[]>([]);
  const [children, setChildren] = useState<CreateCustomerFamilyData[]>([]);
  const [deletedFamilyMembers, setDeletedFamilyMembers] = useState<Array<{ type: 'spouse' | 'children'; index: number; data: any }>>([]);
  const [planningAssets, setPlanningAssets] = useState<CreateCustomerAssetData[]>([]);
  const [existingAssets, setExistingAssets] = useState<CreateCustomerAssetData[]>([]);
  const [deletedAssets, setDeletedAssets] = useState<Array<{ category: 'planning' | 'existing'; index: number; data: any }>>([]);
  const [contacts, setContacts] = useState<CreateCustomerContactData[]>([]);
  const [financials, setFinancials] = useState<CreateCustomerFinancialData[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [changeRequests, setChangeRequests] = useState<CustomerChangeRequest[]>([]);
  const [pendingChanges, setPendingChanges] = useState<Record<string, string>>({});
  const [familyPendingChanges, setFamilyPendingChanges] = useState<Record<string, string>>({});
  const [assetsPendingChanges, setAssetsPendingChanges] = useState<Record<string, string>>({});
  const [pendingApprovals, setPendingApprovals] = useState<number[]>([]); // Change request IDs to approve
  const [pendingRejections, setPendingRejections] = useState<number[]>([]); // Change request IDs to reject
  const [reportingFiles, setReportingFiles] = useState<{
    identification_passport?: UploadFile[];
    bank?: UploadFile[];
    address?: UploadFile[];
    signature?: UploadFile[];
    tax?: UploadFile[];
  }>({});
  const [familyFiles, setFamilyFiles] = useState<Record<string, UploadFile[]>>({});
  const [customerName, setCustomerName] = useState<string>('');
  const [customerType, setCustomerType] = useState<'potential' | 'active' | 'inactive'>('potential');
  const [isEditing, setIsEditing] = useState(!Boolean(id)); // For new customers, default to editing mode
  const [activeTab, setActiveTab] = useState<string>(searchParams.get('tab') || 'overview');
  const navigate = useNavigate();

  useEffect(() => {
    fetchLists();
  }, []);

  // Sync activeTab with URL parameter
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Handle tab change and update URL
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setSearchParams({ tab: key });
  };

  useEffect(() => {
    if (isEdit && id) {
      loadCustomer(parseInt(id, 10));
    } else {
      form.setFieldsValue({ enabled: true, type: 'potential' });
      setCustomerType('potential');
      setIsEditing(true); // For new customers, enable editing by default
    }
  }, [isEdit, id, form]);

  const fetchLists = async () => {
    try {
      const [usersResponse, banksResponse, teamsResponse] = await Promise.all([
        api.get('/users').catch(() => ({ data: { data: [] } })),
        bankService.getAll(),
        api.get('/teams'),
      ]);
      setStaff(usersResponse.data?.data || usersResponse.data || []);
      setBanks(banksResponse || []);
      setTeams(teamsResponse.data?.data || teamsResponse.data || []);
    } catch {
      message.warning('Unable to load dropdown lists');
    }
  };

  const loadCustomer = async (customerId: number) => {
    setLoadingCustomer(true);
    try {
      const customer = await customerService.getById(customerId);
      const { password, ...customerData } = customer; // Exclude password from form data
      
      // Convert engagement string to array if needed
      let engagementValue: string | string[] | undefined = customerData.engagement;
      if (engagementValue && typeof engagementValue === 'string') {
        // If engagement is a comma-separated string, split it
        engagementValue = engagementValue.split(',').map(s => s.trim()).filter(s => s);
      } else if (!engagementValue) {
        engagementValue = [];
      }
      
      // Get first contact for single contact fields
      const firstContact = customer.contacts && customer.contacts.length > 0 ? customer.contacts[0] : null;
      
      // Get first financial (bank) for single bank fields
      const firstFinancial = customer.financials && customer.financials.length > 0 ? customer.financials[0] : null;
      
      form.setFieldsValue({
        ...customerData,
        engagement: engagementValue,
        contact_number: firstContact?.contact_number || '',
        email: customerData.email || firstContact?.email || '',
        address1: firstContact?.address1 || '',
        address2: firstContact?.address2 || '',
        bank_id: firstFinancial?.bank_id || undefined,
        branch_name: firstFinancial?.branch_name || '',
        bank_number: firstFinancial?.bank_number || '',
        tax_date: customerData.tax_date ? dayjs(customerData.tax_date).startOf('day') : undefined,
        agent_staff_id: customerData.agent_staff_id || undefined,
        agent_appoint_date: customerData.agent_appoint_date ? dayjs(customerData.agent_appoint_date).startOf('day') : undefined,
        agent_resign_date: customerData.agent_resign_date ? dayjs(customerData.agent_resign_date).startOf('day') : undefined,
        staff_id: customerData.staff_id || undefined,
        appoint_date: customerData.appoint_date ? dayjs(customerData.appoint_date).startOf('day') : undefined,
        resign_date: customerData.resign_date ? dayjs(customerData.resign_date).startOf('day') : undefined,
        enabled: customer.enabled,
        reporting: {
          bank: customer.reporting?.bank_text || '',
          visa: customer.reporting?.visa_text || '',
        },
      } as any);
      
      // Set customer name and type for header display
      setCustomerName(customer.name);
      setCustomerType(customer.type || 'potential');
      
      // Separate family members by type
      const familyMembers = customer.family || [];
      const spousesData = familyMembers.filter(f => f.type === 'spouse');
      const childrenData = familyMembers.filter(f => f.type === 'children');
      setSpouses(spousesData);
      setChildren(childrenData);
      
      // Load family attachment files
      const familyFilesData: Record<string, UploadFile[]> = {};
      spousesData.forEach((spouse, index) => {
        if (spouse.attachment) {
          familyFilesData[`spouse_${index}`] = [{
            uid: `-spouse-${index}-${Date.now()}`,
            name: 'Attachment',
            status: 'done',
          }];
        }
      });
      childrenData.forEach((child, index) => {
        if (child.attachment) {
          familyFilesData[`children_${index}`] = [{
            uid: `-children-${index}-${Date.now()}`,
            name: 'Attachment',
            status: 'done',
          }];
        }
      });
      setFamilyFiles(familyFilesData);
      
      // Separate assets by category
      const assets = customer.assets || [];
      setPlanningAssets(assets.filter(a => a.category === 'planning'));
      setExistingAssets(assets.filter(a => a.category === 'existing'));
      
      // Load contacts
      setContacts(customer.contacts || []);
      
      // Load financials
      setFinancials(customer.financials || []);
      
      // Load change requests
      setChangeRequests(customer.change_requests || []);
      
      // Load reporting files
      const reporting = customer.reporting;
      if (reporting) {
        const fileFields = ['identification_passport', 'bank', 'address', 'tax'] as const;
        const files: typeof reportingFiles = {};
        fileFields.forEach(field => {
          const value = (reporting as any)[field];
          if (value) {
            // For supporting documents, we don't need to set a URL
            // The SupportingTab component will use the API endpoint directly
            // Just store a placeholder to indicate file exists
            files[field as keyof typeof files] = [{
              uid: `-${field}-${Date.now()}`,
              name: field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
              status: 'done',
              // Don't set url - we'll use API endpoint instead
            }];
          }
        });
        
        // Handle signature separately (like business company_chop)
        if (reporting.signature) {
          const s3Path = reporting.signature;
          if (s3Path && typeof s3Path === 'string') {
            // Extract filename from S3 path
            const filename = s3Path.split('/').pop() || 'signature';
            files.signature = [{
              uid: `-signature`,
              name: filename,
              status: 'done',
              url: s3Path, // Store raw S3 path, not a URL
            } as UploadFile];
          }
        }
        
        setReportingFiles(files);
      }
    } catch {
      message.error('Failed to load customer');
      navigate('/customers');
    } finally {
      setLoadingCustomer(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    
    try {
      await customerService.delete(parseInt(id, 10));
      message.success('Customer deleted');
      navigate('/customers');
    } catch {
      message.error('Failed to delete customer');
    }
  };

  // Calculate customer status based on engagement dates
  const calculateCustomerStatus = (): 'potential' | 'active' | 'inactive' => {
    const allValues = form.getFieldsValue();
    
    // Only advisor row controls status.
    const agentAppointDate = allValues.agent_appoint_date;
    const agentResignDate = allValues.agent_resign_date;
    
    // Check if any resign date exists (priority: highest)
    const hasAnyResignDate = agentResignDate !== null && agentResignDate !== undefined;
    
    // Check if any appoint date exists
    const hasAnyAppointDate = agentAppointDate !== null && agentAppointDate !== undefined;
    
    // If any row has resign date, status is inactive
    if (hasAnyResignDate) {
      return 'inactive';
    }
    
    // If any row has appoint date, status is active
    if (hasAnyAppointDate) {
      return 'active';
    }
    
    // If no appoint dates, status is potential
    return 'potential';
  };

  const handleEngagementDateChange = () => {
    const newStatus = calculateCustomerStatus();
    form.setFieldsValue({ type: newStatus });
    setCustomerType(newStatus);
  };

  const handleSubmit = async (values: CreateCustomerData) => {
    setSubmitting(true);
    try {
      // Get all add change requests (both pending and approved) to filter them out from arrays
      // Add requests (both pending and approved) will be processed via approveChangeRequest API,
      // so we should exclude them from the submit data to avoid duplicate insertion
      const addRequests = changeRequests.filter(cr => 
        (cr.status === 'pending' || cr.status === 'approved') && 
        (cr.field_name?.endsWith('_add') || 
         cr.field_name?.match(/^family_(spouse|children)_\d+_add$/) ||
         cr.field_name?.match(/^asset_(planning|existing)_\d+_add$/))
      );
      
      // Create sets to track items that should be excluded (from all add requests)
      const excludedFamilyItems = new Set<string>();
      const excludedAssetItems = new Set<string>();
      
      addRequests.forEach(cr => {
        try {
          const newValue = JSON.parse(cr.new_value || '{}');
          if (cr.field_name?.match(/^family_(spouse|children)_\d+_add$/)) {
            const key = `${newValue.name || ''}_${newValue.identification_no || ''}`;
            excludedFamilyItems.add(key);
          } else if (cr.field_name?.match(/^asset_(planning|existing)_\d+_add$/)) {
            const key = `${newValue.type || ''}_${newValue.plate_no || ''}_${newValue.model || ''}`;
            excludedAssetItems.add(key);
          }
        } catch (e) {
          // Ignore parsing errors
        }
      });
      
      // Combine spouses and children into family array
      // Exclude items from add requests (both pending and approved) - they will be processed via approveChangeRequest API
      const family = [
        ...spouses
          .map(s => ({ ...s, type: 'spouse' as const }))
          .filter(s => {
            const key = `${s.name || ''}_${s.identification_no || ''}`;
            return !excludedFamilyItems.has(key);
          }),
        ...children
          .map(c => ({ ...c, type: 'children' as const }))
          .filter(c => {
            const key = `${c.name || ''}_${c.identification_no || ''}`;
            return !excludedFamilyItems.has(key);
          }),
      ];

      // Combine planning and existing assets
      // Exclude items from add requests (both pending and approved) - they will be processed via approveChangeRequest API
      const assets = [
        ...planningAssets
          .map(a => ({ ...a, category: 'planning' as const }))
          .filter(a => {
            const key = `${a.type || ''}_${a.plate_no || ''}_${a.model || ''}`;
            return !excludedAssetItems.has(key);
          }),
        ...existingAssets
          .map(a => ({ ...a, category: 'existing' as const }))
          .filter(a => {
            const key = `${a.type || ''}_${a.plate_no || ''}_${a.model || ''}`;
            return !excludedAssetItems.has(key);
          }),
      ];

      // Contacts
      const contactsData = contacts.length > 0 ? contacts : undefined;

      // Financials
      const financialsData = financials.length > 0 ? financials : undefined;

      // Check if there are files to upload (reporting files or family files)
      const hasReportingFiles = Object.values(reportingFiles).some(files => files && files.length > 0 && files[0].originFileObj);
      const hasFamilyFiles = Object.values(familyFiles).some(files => files && files.length > 0 && files[0].originFileObj);
      const hasFiles = hasReportingFiles || hasFamilyFiles;
      
      console.log('=== File Upload Check ===');
      console.log('reportingFiles:', reportingFiles);
      console.log('familyFiles:', familyFiles);
      console.log('hasReportingFiles:', hasReportingFiles);
      console.log('hasFamilyFiles:', hasFamilyFiles);
      console.log('hasFiles:', hasFiles);
      Object.entries(reportingFiles).forEach(([field, files]) => {
        if (files && files.length > 0) {
          const file = files[0];
          console.log(`Field: ${field}`, {
            hasOriginFileObj: !!file.originFileObj,
            fileName: file.originFileObj?.name || file.name,
            fileSize: file.originFileObj?.size || file.size,
            url: file.url,
          });
        }
      });
      
      if (hasFiles) {
        // Use FormData for file upload
        const formData = new FormData();
        
        // Add all form fields
        Object.entries(values).forEach(([key, value]) => {
          // In edit mode, skip password if it's empty (to keep current password)
          if (isEdit && key === 'password' && (!value || (typeof value === 'string' && value.trim() === ''))) {
            return;
          }
          
          // Skip single contact fields (they'll be added to contacts array)
          if (key === 'contact_number' || key === 'address1' || key === 'address2') {
            return;
          }
          
          // Skip single bank fields (they'll be added to financials array)
          if (key === 'bank_id' || key === 'branch_name' || key === 'bank_number') {
            return;
          }
          
          // Skip family array - it will be handled separately with file uploads
          if (key === 'family' && Array.isArray(value)) {
            return;
          }
          
          // Skip assets array - it will be handled separately
          if (key === 'assets' && Array.isArray(value)) {
            return;
          }
          
          // Handle reporting object (for reporting.bank text field)
          if (key === 'reporting' && typeof value === 'object' && !Array.isArray(value)) {
            Object.entries(value).forEach(([reportKey, reportValue]) => {
              if (reportValue !== undefined && reportValue !== null) {
                formData.append(`reporting[${reportKey}]`, reportValue as string);
              }
            });
            return;
          }
          
          // Skip engagement if it's an array (will be converted to string)
          if (key === 'engagement' && Array.isArray(value)) {
            formData.append(key, value.join(','));
            return;
          }
          
          // Handle engagement date fields explicitly
          const engagementDateFields = ['tax_date', 'agent_appoint_date', 'agent_resign_date', 'appoint_date', 'resign_date'];
          
          if (engagementDateFields.includes(key)) {
            if (dayjs.isDayjs(value)) {
              formData.append(key, value.format('YYYY-MM-DD'));
            } else if (value === null || value === undefined) {
              // Explicitly handle null/undefined for date fields - don't append to allow backend to set to null
              // Laravel will treat missing fields as null for nullable columns
            } else if (typeof value === 'string') {
              formData.append(key, value);
            }
            return; // Skip default handling for date fields
          }
          
          if (value !== undefined && value !== null) {
            // Handle engagement array - convert to comma-separated string
            if (key === 'engagement' && Array.isArray(value)) {
              formData.append(key, value.join(','));
            } else if (typeof value === 'object' && !Array.isArray(value)) {
              formData.append(key, JSON.stringify(value));
            } else if (Array.isArray(value)) {
              value.forEach((item, index) => {
                Object.entries(item).forEach(([itemKey, itemValue]) => {
                  formData.append(`${key}[${index}][${itemKey}]`, itemValue as string);
                });
              });
            } else {
              formData.append(key, value as string);
            }
          }
        });
        
        // Ensure type is included (in case it was changed via header)
        if (customerType) {
          formData.append('type', customerType);
        }

        // Add family
        if (family.length > 0) {
          // Track indices for spouses and children separately
          let spouseIndex = 0;
          let childIndex = 0;
          
          console.log('=== Adding Family to FormData ===');
          console.log('family array:', family);
          console.log('familyFiles:', familyFiles);
          
          family.forEach((item, index) => {
            Object.entries(item).forEach(([key, value]) => {
              if (key !== 'attachment') {
              formData.append(`family[${index}][${key}]`, value as string);
              }
            });
            
            // Handle family attachment file
            const familyType = item.type;
            const currentIndex = familyType === 'spouse' ? spouseIndex : childIndex;
            const fileKey = `${familyType}_${currentIndex}`;
            const files = familyFiles[fileKey];
            
            console.log(`Family member ${index}:`, {
              type: familyType,
              currentIndex,
              fileKey,
              hasFiles: !!files,
              filesLength: files?.length || 0,
              hasOriginFileObj: files?.[0]?.originFileObj ? true : false,
              hasAttachment: !!item.attachment,
            });
            
            if (files && files.length > 0 && files[0].originFileObj) {
              console.log(`Adding file for family[${index}][attachment]:`, {
                fileName: files[0].originFileObj.name,
                fileSize: files[0].originFileObj.size,
                fileType: files[0].originFileObj.type,
              });
              formData.append(`family[${index}][attachment]`, files[0].originFileObj);
            } else if (item.attachment) {
              // Keep existing attachment path
              console.log(`Keeping existing attachment path for family[${index}][attachment]:`, item.attachment);
              formData.append(`family[${index}][attachment]`, item.attachment);
            } else {
              console.log(`No attachment for family member ${index}`);
            }
            
            // Increment the appropriate index
            if (familyType === 'spouse') {
              spouseIndex++;
            } else {
              childIndex++;
            }
          });
        }

        // Add assets
        if (assets.length > 0) {
          assets.forEach((item, index) => {
            Object.entries(item).forEach(([key, value]) => {
              if (value !== undefined && value !== null) {
                formData.append(`assets[${index}][${key}]`, value.toString());
              }
            });
          });
        }

        // Add contact from single contact fields
        const formValues = values as any;
        const contactData = formValues.contact_number || formValues.email || formValues.address1 || formValues.address2
          ? [{
              contact_number: formValues.contact_number || '',
              email: formValues.email || '',
              address1: formValues.address1 || '',
              address2: formValues.address2 || '',
            }]
          : contacts.length > 0 ? contacts : [];
        
        if (contactData.length > 0) {
          contactData.forEach((item, index) => {
            Object.entries(item).forEach(([key, value]) => {
              if (value !== undefined && value !== null) {
                formData.append(`contacts[${index}][${key}]`, value as string);
              }
            });
          });
        }

        // Add financial (bank) from single bank fields
        const financialData = formValues.bank_id || formValues.branch_name || formValues.bank_number
          ? [{
              bank_id: formValues.bank_id,
              branch_name: formValues.branch_name || '',
              bank_number: formValues.bank_number || '',
            }]
          : financials.length > 0 ? financials : [];
        
        if (financialData.length > 0) {
          financialData.forEach((item, index) => {
            Object.entries(item).forEach(([key, value]) => {
              if (value !== undefined && value !== null) {
                formData.append(`financials[${index}][${key}]`, value.toString());
              }
            });
          });
        }

        // Add reporting files (only new files with originFileObj, same as business company_chop)
        Object.entries(reportingFiles).forEach(([field, files]) => {
          if (files && files.length > 0 && files[0].originFileObj) {
            const file = files[0].originFileObj;
            formData.append(field, file);
          }
        });
        
        // Log FormData contents (for debugging)
        console.log('=== FormData Contents ===');
        console.log('FormData entries:');
        const formDataEntries: [string, FormDataEntryValue][] = [];
        formData.forEach((value, key) => {
          formDataEntries.push([key, value]);
        });
        formDataEntries.forEach(([key, value]) => {
          if (value instanceof File) {
            console.log(`  ${key}:`, {
              dataType: 'File',
              name: value.name,
              size: value.size,
              mimeType: value.type,
            });
          } else {
            console.log(`  ${key}:`, value);
          }
        });

        if (isEdit && id) {
          console.log('=== Sending Update Request ===');
          console.log('Customer ID:', id);
          console.log('Using FormData:', true);
          try {
            const result = await customerService.update(parseInt(id, 10), formData);
            console.log('Update response:', result);
            // Reload customer data to get updated files
            await loadCustomer(parseInt(id, 10));
            // Switch to view mode after successful save
            setIsEditing(false);
            // Show success message
            message.success('Customer updated successfully', 5);
          } catch (error: any) {
            console.error('Update error:', error);
            console.error('Error response:', error.response?.data);
            throw error;
          }
        } else {
          const newCustomer = await customerService.create(formData);
          message.success('Customer created');
          // Navigate to edit page for the new customer
          navigate(`/customers/${newCustomer.id}`);
        }
      } else {
        // No files, use regular JSON
        // Convert engagement array to string if needed (or keep as array if backend supports it)
        const engagementValue = Array.isArray(values.engagement) 
          ? values.engagement.join(',') 
          : values.engagement;
        
        // Create contact from single contact fields
        const formValues = values as any;
        // Check if form has contact fields (even if empty, they should be saved)
        const hasContactFields = formValues.hasOwnProperty('contact_number') || 
                                 formValues.hasOwnProperty('email') || 
                                 formValues.hasOwnProperty('address1') || 
                                 formValues.hasOwnProperty('address2');
        
        // If form has contact fields, use them; otherwise use contactsData if it exists
        // Always create contactData if hasContactFields is true, even if all values are empty
        let contactData: any[] | undefined;
        if (hasContactFields) {
          contactData = [{
            contact_number: formValues.contact_number || null,
            email: formValues.email || null,
            address1: formValues.address1 || null,
            address2: formValues.address2 || null,
          }];
        } else if (contactsData) {
          contactData = contactsData;
        }
        
        // Create financial (bank) from single bank fields
        const financialData = formValues.bank_id || formValues.branch_name || formValues.bank_number
          ? [{
              bank_id: formValues.bank_id,
              branch_name: formValues.branch_name || '',
              bank_number: formValues.bank_number || '',
            }]
          : financialsData;
        
        // Convert date fields from dayjs to string if needed
        const convertDateField = (dateValue: any): string | null => {
          if (!dateValue) return null;
          if (dayjs.isDayjs(dateValue)) {
            return dateValue.format('YYYY-MM-DD');
          }
          if (typeof dateValue === 'string') {
            return dateValue;
          }
          return null;
        };
        
        const submitData: any = {
          ...values,
          engagement: engagementValue,
          type: customerType, // Ensure type is included (in case it was changed via header)
          tax_date: convertDateField(values.tax_date),
          agent_appoint_date: convertDateField(values.agent_appoint_date),
          agent_resign_date: convertDateField(values.agent_resign_date),
          appoint_date: convertDateField(values.appoint_date),
          resign_date: convertDateField(values.resign_date),
          family: family.length > 0 ? family : undefined,
          assets: assets.length > 0 ? assets : undefined,
          contacts: contactData || undefined, // Ensure contacts is sent even if empty array
          financials: financialData || undefined, // Ensure financials is sent even if empty array
        };
        
        // Remove single contact fields from submitData (they're now in contacts array)
        delete submitData.contact_number;
        delete submitData.address1;
        delete submitData.address2;
        
        // Remove single bank fields from submitData (they're now in financials array)
        delete submitData.bank_id;
        delete submitData.branch_name;
        delete submitData.bank_number;
        
        // Ensure reporting object is properly structured (for reporting.bank text field)
        if (submitData.reporting && typeof submitData.reporting === 'object') {
          // Keep reporting object as is - it will be handled by backend
        }

        // In edit mode, if password is empty, don't send it (to keep current password)
        if (isEdit && (!submitData.password || submitData.password.trim() === '')) {
          delete submitData.password;
        }

        if (isEdit && id) {
          await customerService.update(parseInt(id, 10), submitData);
          // Reload customer data and switch to view mode
          await loadCustomer(parseInt(id, 10));
          setIsEditing(false);
          // Show success message
          message.success('Customer updated successfully', 5);
        } else {
          const newCustomer = await customerService.create(submitData);
          // Show success message
          message.success('Customer created successfully', 5);
          // Navigate back to customers listing page
          navigate('/customers');
        }
      }
      
      // Process pending change request approvals and rejections
      if (pendingApprovals.length > 0 || pendingRejections.length > 0) {
        try {
          // Process approvals - need to reload change requests to get original pending status
          // But we can use the IDs from pendingApprovals to find them
          for (const requestId of pendingApprovals) {
            // Find the change request (it may have been UI-updated to 'approved', but we need to approve it in DB)
            // We'll reload the customer data after all approvals/rejections, so we can just call the API
            // The API will handle the actual database update
            await customerService.approveChangeRequest(requestId);
          }
          
          // Process rejections
          for (const requestId of pendingRejections) {
            await customerService.rejectChangeRequest(requestId);
          }
          
          // Clear pending lists
          setPendingApprovals([]);
          setPendingRejections([]);
          
          // Reload customer data to get updated change requests
          if (isEdit && id) {
            await loadCustomer(parseInt(id, 10));
            setIsEditing(false);
          }
        } catch (error) {
          console.error('Error processing change requests:', error);
          message.warning('Customer saved, but some change requests could not be processed');
        }
      } else if (isEdit && id) {
        // If no change requests to process, just switch to view mode
        // Keep current tab in URL after save
        setSearchParams({ tab: activeTab });
        setIsEditing(false);
      }
    } catch {
      message.error(isEdit ? 'Failed to update customer' : 'Failed to create customer');
    } finally {
      setSubmitting(false);
    }
  };

  const addContact = () => {
    setContacts([...contacts, { contact_number: '', email: '', address1: '', address2: '' }]);
  };

  const removeContact = (index: number) => {
    setContacts(contacts.filter((_, i) => i !== index));
  };

  const updateContact = (index: number, field: keyof CreateCustomerContactData, value: string) => {
    const updated = [...contacts];
    updated[index] = { ...updated[index], [field]: value };
    setContacts(updated);
  };

  const addFinancial = () => {
    setFinancials([...financials, { bank_id: undefined, branch_name: '', bank_number: '' }]);
  };

  const removeFinancial = (index: number) => {
    setFinancials(financials.filter((_, i) => i !== index));
  };

  const updateFinancial = (index: number, field: keyof CreateCustomerFinancialData, value: string | number | undefined) => {
    const updated = [...financials];
    updated[index] = { ...updated[index], [field]: value };
    setFinancials(updated);
  };

  // Save family data immediately to database (admin panel only)
  const saveFamilyImmediately = async () => {
    if (!isEdit || !id) return;
    
    try {
      const family = [
        ...spouses.map(s => ({ ...s, type: 'spouse' as const })),
        ...children.map(c => ({ ...c, type: 'children' as const })),
      ];
      
      await customerService.update(parseInt(id, 10), { family });
      message.success('Family updated');
    } catch (error) {
      message.error('Failed to save family');
      console.error('Error saving family:', error);
    }
  };

  const addSpouse = async () => {
    const newSpouses = [...spouses, { type: 'spouse' as const, name: '', identification_no: '' }];
    setSpouses(newSpouses);
    // Save immediately in admin panel
    if (isEdit && id) {
      await saveFamilyImmediately();
    }
  };

  const removeSpouse = async (index: number) => {
    const newSpouses = spouses.filter((_, i) => i !== index);
    setSpouses(newSpouses);
    // Save immediately in admin panel
    if (isEdit && id) {
      await saveFamilyImmediately();
    }
  };

  const updateSpouse = (index: number, field: keyof CreateCustomerFamilyData, value: string) => {
    const updated = [...spouses];
    updated[index] = { ...updated[index], [field]: value };
    setSpouses(updated);
  };

  // Debounce timer for family field changes
  const familySaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleFamilyFieldChange = async (type: 'spouse' | 'children', index: number, field: 'name' | 'identification_no', value: string) => {
    const key = `${type}_${index}_${field}`;
    setFamilyPendingChanges(prev => ({
      ...prev,
      [key]: value,
    }));
    
    // Update local state immediately
    if (type === 'spouse') {
      updateSpouse(index, field, value);
    } else {
      updateChild(index, field, value);
    }
    
    // Save immediately in admin panel with debounce
    if (isEdit && id) {
      // Clear previous timer
      if (familySaveTimerRef.current) {
        clearTimeout(familySaveTimerRef.current);
      }
      // Set new timer to save after 500ms of no changes
      familySaveTimerRef.current = setTimeout(async () => {
        await saveFamilyImmediately();
        familySaveTimerRef.current = null;
      }, 500);
    }
  };

  const getFamilyDisplayValue = (type: 'spouse' | 'children', index: number, field: 'name' | 'identification_no'): string => {
    const key = `${type}_${index}_${field}`;
    return familyPendingChanges[key] || '';
  };

  const handleApproveFamilyChange = async (type: 'spouse' | 'children', index: number, field: 'name' | 'identification_no') => {
    try {
      const key = `${type}_${index}_${field}`;
      const newValue = familyPendingChanges[key];
      
      if (!newValue) {
        message.warning('No change to approve');
        return;
      }

      if (!isEdit || !id) {
        message.error('Customer ID is required');
        return;
      }

      const familyList = type === 'spouse' ? spouses : children;
      const familyMember = familyList[index];
      const oldValue = familyMember[field] || '';

      if (newValue === oldValue) {
        message.warning('No change to approve');
        return;
      }

      // Create change request and approve it immediately
      const fieldName = `family_${type}_${index}_${field}`;
      await customerService.createChangeRequest({
        customer_id: parseInt(id, 10),
        field_name: fieldName,
        old_value: oldValue || '',
        new_value: newValue,
        status: 'approved',
      });

      // Update the family member locally
      if (type === 'spouse') {
        updateSpouse(index, field, newValue);
      } else {
        updateChild(index, field, newValue);
      }

      // Clear pending change
      const updated = { ...familyPendingChanges };
      delete updated[key];
      setFamilyPendingChanges(updated);

      message.success('Change approved and applied');
      
      // Reload customer data to get updated change requests
      await loadCustomer(parseInt(id, 10));
    } catch {
      message.error('Failed to approve change');
    }
  };

  const handleRejectFamilyChange = async (type: 'spouse' | 'children', index: number, field: 'name' | 'identification_no') => {
    try {
      const key = `${type}_${index}_${field}`;
      const newValue = familyPendingChanges[key];
      
      if (!newValue) {
        const updated = { ...familyPendingChanges };
        delete updated[key];
        setFamilyPendingChanges(updated);
        message.info('Change rejected');
        return;
      }

      if (!isEdit || !id) {
        const updated = { ...familyPendingChanges };
        delete updated[key];
        setFamilyPendingChanges(updated);
        message.info('Change rejected');
        return;
      }

      const familyList = type === 'spouse' ? spouses : children;
      const familyMember = familyList[index];
      const oldValue = familyMember[field] || '';

      // Create change request with rejected status
      const fieldName = `family_${type}_${index}_${field}`;
      await customerService.createChangeRequest({
        customer_id: parseInt(id, 10),
        field_name: fieldName,
        old_value: oldValue || '',
        new_value: newValue,
        status: 'rejected',
      });

      // Clear pending change
      const updated = { ...familyPendingChanges };
      delete updated[key];
      setFamilyPendingChanges(updated);

      message.info('Change rejected');
      
      // Reload customer data to get updated change requests
      await loadCustomer(parseInt(id, 10));
    } catch {
      message.error('Failed to reject change');
    }
  };

  const handleAssetFieldChange = (
    category: 'planning' | 'existing',
    index: number,
    field: 'plate_no' | 'model' | 'address' | 'year' | 'price' | 'installment' | 'type',
    value: string
  ) => {
    const key = `${category}_${index}_${field}`;
    setAssetsPendingChanges(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const getAssetDisplayValue = (
    category: 'planning' | 'existing',
    index: number,
    field: 'plate_no' | 'model' | 'address' | 'year' | 'price' | 'installment' | 'type'
  ): string => {
    const key = `${category}_${index}_${field}`;
    return assetsPendingChanges[key] || '';
  };

  const handleApproveAssetChange = async (category: 'planning' | 'existing', index: number, field: 'plate_no' | 'model' | 'address' | 'year' | 'price' | 'installment') => {
    try {
      const key = `${category}_${index}_${field}`;
      const newValue = assetsPendingChanges[key];
      
      if (!newValue && newValue !== '0') {
        message.warning('No change to approve');
        return;
      }

      if (!isEdit || !id) {
        message.error('Customer ID is required');
        return;
      }

      const assetList = category === 'planning' ? planningAssets : existingAssets;
      const asset = assetList[index];
      
      const oldValue = asset[field]?.toString() || '';
      
      if (newValue === oldValue) {
        message.warning('No change to approve');
        return;
      }

      let parsedValue: string | number | undefined = newValue;
      if (field === 'year') {
        parsedValue = newValue ? parseInt(newValue, 10) : undefined;
      } else if (field === 'price' || field === 'installment') {
        parsedValue = newValue ? parseFloat(newValue) : undefined;
      }

      // Create change request and approve it immediately
      const fieldName = `asset_${category}_${index}_${field}`;
      await customerService.createChangeRequest({
        customer_id: parseInt(id, 10),
        field_name: fieldName,
        old_value: oldValue || '',
        new_value: newValue,
        status: 'approved',
      });

      // Update the asset locally
      if (category === 'planning') {
        updatePlanningAsset(index, field, parsedValue);
      } else {
        updateExistingAsset(index, field, parsedValue);
      }

      // Clear pending change
      const updated = { ...assetsPendingChanges };
      delete updated[key];
      setAssetsPendingChanges(updated);

      message.success('Change approved and applied');
      
      // Reload customer data to get updated change requests
      await loadCustomer(parseInt(id, 10));
    } catch {
      message.error('Failed to approve change');
    }
  };

  const handleRejectAssetChange = async (category: 'planning' | 'existing', index: number, field: 'plate_no' | 'model' | 'address' | 'year' | 'price' | 'installment') => {
    try {
      const key = `${category}_${index}_${field}`;
      const newValue = assetsPendingChanges[key];
      
      if (!newValue && newValue !== '0') {
        const updated = { ...assetsPendingChanges };
        delete updated[key];
        setAssetsPendingChanges(updated);
        message.info('Change rejected');
        return;
      }

      if (!isEdit || !id) {
        const updated = { ...assetsPendingChanges };
        delete updated[key];
        setAssetsPendingChanges(updated);
        message.info('Change rejected');
        return;
      }

      const assetList = category === 'planning' ? planningAssets : existingAssets;
      const asset = assetList[index];
      const oldValue = asset[field]?.toString() || '';

      // Create change request with rejected status
      const fieldName = `asset_${category}_${index}_${field}`;
      await customerService.createChangeRequest({
        customer_id: parseInt(id, 10),
        field_name: fieldName,
        old_value: oldValue || '',
        new_value: newValue,
        status: 'rejected',
      });

      // Clear pending change
      const updated = { ...assetsPendingChanges };
      delete updated[key];
      setAssetsPendingChanges(updated);

      message.info('Change rejected');
      
      // Reload customer data to get updated change requests
      await loadCustomer(parseInt(id, 10));
    } catch {
      message.error('Failed to reject change');
    }
  };

  const addChild = async () => {
    const newChildren = [...children, { type: 'children' as const, name: '', identification_no: '' }];
    setChildren(newChildren);
    // Save immediately in admin panel
    if (isEdit && id) {
      await saveFamilyImmediately();
    }
  };

  const removeChild = async (index: number) => {
    // In admin portal, directly remove (no change request needed)
    const newChildren = children.filter((_, i) => i !== index);
    setChildren(newChildren);
    // Save immediately in admin panel
    if (isEdit && id) {
      await saveFamilyImmediately();
    }
  };

  const handleApproveFamilyDelete = (requestId: number) => {
    // Use the same logic as handleApproveChangeRequest (UI only; persist on Save)
    handleApproveChangeRequest(requestId);
  };

  const handleRejectFamilyDelete = (requestId: number) => {
    // Use the same logic as handleRejectChangeRequest (UI only; persist on Save)
    handleRejectChangeRequest(requestId);
  };

  // Approve/Reject asset change requests (UI only; persist on Save)
  const handleApproveAssetRequests = (ids: number[]) => {
    setChangeRequests(prev => prev.map(cr =>
      ids.includes(cr.id)
        ? { ...cr, status: 'approved', reviewed_at: new Date().toISOString() }
        : cr
    ));
    setPendingApprovals(prev => {
      const merged = [...prev];
      ids.forEach(id => {
        if (!merged.includes(id)) merged.push(id);
      });
      return merged;
    });
    setPendingRejections(prev => prev.filter(id => !ids.includes(id)));
  };

  const handleRejectAssetRequests = (ids: number[]) => {
    setChangeRequests(prev => prev.map(cr =>
      ids.includes(cr.id)
        ? { ...cr, status: 'rejected', reviewed_at: new Date().toISOString() }
        : cr
    ));
    setPendingRejections(prev => {
      const merged = [...prev];
      ids.forEach(id => {
        if (!merged.includes(id)) merged.push(id);
      });
      return merged;
    });
    setPendingApprovals(prev => prev.filter(id => !ids.includes(id)));
    message.info('Change request will be rejected when you click Save');
  };

  const handleApproveAssetDelete = (requestId: number) => {
    // Use the same logic as handleApproveChangeRequest (UI only; persist on Save)
    handleApproveChangeRequest(requestId);
  };

  const handleRejectAssetDelete = (requestId: number) => {
    // Use the same logic as handleRejectChangeRequest (UI only; persist on Save)
    handleRejectChangeRequest(requestId);
  };

  const updateChild = (index: number, field: keyof CreateCustomerFamilyData, value: string) => {
    const updated = [...children];
    updated[index] = { ...updated[index], [field]: value };
    setChildren(updated);
  };

  // Change family member type (spouse <-> children)
  const handleFamilyTypeChange = async (type: 'spouse' | 'children', index: number, newType: 'spouse' | 'children') => {
    if (type === newType) return;

    if (type === 'spouse') {
      const member = spouses[index];
      if (!member) return;
      const newSpouses = spouses.filter((_, i) => i !== index);
      const newChildren = [...children, { ...member, type: 'children' as const }];
      setSpouses(newSpouses);
      setChildren(newChildren);
    } else {
      const member = children[index];
      if (!member) return;
      const newChildren = children.filter((_, i) => i !== index);
      const newSpouses = [...spouses, { ...member, type: 'spouse' as const }];
      setChildren(newChildren);
      setSpouses(newSpouses);
    }

    // Save immediately in admin panel
    if (isEdit && id) {
      await saveFamilyImmediately();
    }

    // Clear pending field changes for this member's previous type/index
    setFamilyPendingChanges(prev => {
      const updated = { ...prev };
      delete updated[`${type}_${index}_name`];
      delete updated[`${type}_${index}_identification_no`];
      return updated;
    });
  };

  const addPlanningAsset = () => {
    setPlanningAssets([...planningAssets, { category: 'planning', type: 'motor_vehicle', plate_no: '', model: '', address: '', year: undefined, price: undefined, installment_no: undefined, installment: undefined }]);
  };

  const removePlanningAsset = (index: number) => {
    setPlanningAssets(planningAssets.filter((_, i) => i !== index));
  };

  const updatePlanningAsset = (index: number, field: keyof CreateCustomerAssetData, value: string | number | undefined) => {
    const updated = [...planningAssets];
    updated[index] = { ...updated[index], [field]: value };
    setPlanningAssets(updated);
  };

  const addExistingAsset = () => {
    setExistingAssets([...existingAssets, { category: 'existing', type: 'motor_vehicle', plate_no: '', model: '', address: '', year: undefined, price: undefined, installment_no: undefined, installment: undefined }]);
  };

  const removeExistingAsset = (index: number) => {
    setExistingAssets(existingAssets.filter((_, i) => i !== index));
  };

  const updateExistingAsset = (index: number, field: keyof CreateCustomerAssetData, value: string | number | undefined) => {
    const updated = [...existingAssets];
    updated[index] = { ...updated[index], [field]: value };
    setExistingAssets(updated);
  };

  const handleApproveChangeRequest = (requestId: number) => {
    const changeRequest = changeRequests.find(cr => cr.id === requestId);
    if (!changeRequest) return;
    
    const fieldName = changeRequest.field_name;
    const newValue = changeRequest.new_value;
    
    // Immediately update UI to show approved status
    setChangeRequests(prev => prev.map(cr => 
      cr.id === requestId 
        ? { ...cr, status: 'approved' as const, reviewed_at: new Date().toISOString() }
        : cr
    ));
    
    // Handle different types of change requests
    if (fieldName?.endsWith('_delete')) {
      // Handle delete requests: mark item for deletion in UI
      const match = fieldName.match(/^(family|asset)_(spouse|children|planning|existing)_(\d+)_delete$/);
      if (match) {
        const [, type, subType, indexStr] = match;
        const originalIndex = parseInt(indexStr, 10);
        
        if (type === 'family') {
          const familyList = subType === 'spouse' ? spouses : children;
          // Calculate current index accounting for deleted items
          let currentIndex = originalIndex;
          deletedFamilyMembers.forEach(deleted => {
            if (deleted.type === subType && deleted.index < originalIndex) {
              currentIndex--;
            }
          });
          
          const memberToDelete = familyList[currentIndex];
          if (memberToDelete) {
            // Only mark for deletion, don't remove from array yet (will be processed on Save)
            setDeletedFamilyMembers(prev => {
              const exists = prev.some(d => d.type === subType && d.index === originalIndex);
              if (exists) return prev;
              return [...prev, { type: subType as 'spouse' | 'children', index: originalIndex, data: memberToDelete }];
            });
          }
        } else if (type === 'asset') {
          const assetList = subType === 'planning' ? planningAssets : existingAssets;
          // Calculate current index accounting for deleted items
          let currentIndex = originalIndex;
          // Note: deletedAssets uses category and index, need to check
          deletedAssets.forEach(deleted => {
            if (deleted.category === subType && deleted.index < originalIndex) {
              currentIndex--;
            }
          });
          
          const assetToDelete = assetList[currentIndex];
          if (assetToDelete) {
            // Only mark for deletion, don't remove from array yet (will be processed on Save)
            setDeletedAssets(prev => {
              const exists = prev.some(d => d.category === subType && d.index === originalIndex);
              if (exists) return prev;
              return [...prev, { category: subType as 'planning' | 'existing', index: originalIndex, data: assetToDelete }];
            });
          }
        }
      }
    } else if (fieldName === 'family_spouse_add' || fieldName === 'family_children_add' || 
               fieldName?.match(/^family_(spouse|children)_\d+_add$/)) {
      // Handle family add requests
      try {
        const newMember = JSON.parse(newValue || '{}');
        if (newMember && (newMember.name || newMember.identification_no)) {
          if (fieldName.includes('spouse')) {
            setSpouses(prev => {
              const existingKeys = new Set(prev.map(s => `${s.name || ''}_${s.identification_no || ''}`));
              const key = `${newMember.name || ''}_${newMember.identification_no || ''}`;
              if (key.trim() && !existingKeys.has(key)) {
                return [...prev, newMember];
              }
              return prev;
            });
          } else {
            setChildren(prev => {
              const existingKeys = new Set(prev.map(c => `${c.name || ''}_${c.identification_no || ''}`));
              const key = `${newMember.name || ''}_${newMember.identification_no || ''}`;
              if (key.trim() && !existingKeys.has(key)) {
                return [...prev, newMember];
              }
              return prev;
            });
          }
        }
      } catch (e) {
        console.error('Error parsing family add request:', e);
      }
    } else if (fieldName === 'asset_planning_add' || fieldName === 'asset_existing_add' ||
               fieldName?.match(/^asset_(planning|existing)_\d+_add$/)) {
      // Handle asset add requests
      try {
        const newAsset = JSON.parse(newValue || '{}');
        if (newAsset && (newAsset.type || newAsset.plate_no || newAsset.model)) {
          const category = fieldName.includes('planning') ? 'planning' : 'existing';
          if (category === 'planning') {
            setPlanningAssets(prev => {
              const existingKeys = new Set(prev.map(a => `${a.type || ''}_${a.plate_no || ''}_${a.model || ''}`));
              const key = `${newAsset.type || ''}_${newAsset.plate_no || ''}_${newAsset.model || ''}`;
              if (key.trim() && !existingKeys.has(key)) {
                return [...prev, newAsset];
              }
              return prev;
            });
          } else {
            setExistingAssets(prev => {
              const existingKeys = new Set(prev.map(a => `${a.type || ''}_${a.plate_no || ''}_${a.model || ''}`));
              const key = `${newAsset.type || ''}_${newAsset.plate_no || ''}_${newAsset.model || ''}`;
              if (key.trim() && !existingKeys.has(key)) {
                return [...prev, newAsset];
              }
              return prev;
            });
          }
        }
      } catch (e) {
        console.error('Error parsing asset add request:', e);
      }
    } else if (fieldName) {
      // Handle field modification requests
      const familyMatch = fieldName.match(/^family_(spouse|children)_(\d+)_(name|identification_no)$/);
      if (familyMatch) {
        const [, type, indexStr, field] = familyMatch;
        const index = parseInt(indexStr, 10);
        const familyList = type === 'spouse' ? spouses : children;
        
        // Calculate current index accounting for deleted items
        let currentIndex = index;
        deletedFamilyMembers.forEach(deleted => {
          if (deleted.type === type && deleted.index < index) {
            currentIndex--;
          }
        });
        
        if (type === 'spouse' && spouses[currentIndex]) {
          const updated = [...spouses];
          updated[currentIndex] = { ...updated[currentIndex], [field]: newValue };
          setSpouses(updated);
        } else if (type === 'children' && children[currentIndex]) {
          const updated = [...children];
          updated[currentIndex] = { ...updated[currentIndex], [field]: newValue };
          setChildren(updated);
        }
      } else {
        const assetMatch = fieldName.match(/^asset_(planning|existing)_(\d+)_(.+)$/);
        if (assetMatch) {
          const [, category, indexStr, field] = assetMatch;
          const index = parseInt(indexStr, 10);
          const assetList = category === 'planning' ? planningAssets : existingAssets;
          
          // Calculate current index accounting for deleted items
          let currentIndex = index;
          deletedAssets.forEach(deleted => {
            if (deleted.category === category && deleted.index < index) {
              currentIndex--;
            }
          });
          
          if (category === 'planning' && planningAssets[currentIndex]) {
            const updated = [...planningAssets];
            updated[currentIndex] = { ...updated[currentIndex], [field]: newValue };
            setPlanningAssets(updated);
          } else if (category === 'existing' && existingAssets[currentIndex]) {
            const updated = [...existingAssets];
            updated[currentIndex] = { ...updated[currentIndex], [field]: newValue };
            setExistingAssets(updated);
          }
        } else {
          // Handle overview field changes
          form.setFieldValue(fieldName as any, newValue);
        }
      }
    }
    
    // Mark for approval (will be processed on Save)
    setPendingApprovals(prev => {
      if (prev.includes(requestId)) return prev;
      return [...prev, requestId];
    });
    setPendingRejections(prev => prev.filter(id => id !== requestId));
    message.info('Change request will be approved when you click Save');
  };

  const handleApprovePendingChange = async (fieldName: string) => {
    try {
      const newValue = pendingChanges[fieldName];
      const oldValue = form.getFieldValue(fieldName as any);
      
      if (!newValue || newValue === oldValue) {
        message.warning('No change to approve');
        return;
      }

      // Create change request and approve it immediately
      await customerService.createChangeRequest({
        customer_id: parseInt(id!, 10),
        field_name: fieldName,
        old_value: oldValue || '',
        new_value: newValue,
        status: 'approved',
      });

      // Update form field value
      form.setFieldValue(fieldName as any, newValue);
      
      // Clear pending change
      const updated = { ...pendingChanges };
      delete updated[fieldName];
      setPendingChanges(updated);

      message.success('Change approved and applied');
      
      // Reload customer data to get updated change requests
      if (isEdit && id) {
        await loadCustomer(parseInt(id, 10));
      }
    } catch {
      message.error('Failed to approve change');
    }
  };

  const handleRejectPendingChange = (fieldName: string) => {
    const updated = { ...pendingChanges };
    delete updated[fieldName];
    setPendingChanges(updated);
    message.info('Change rejected');
  };

  const handleFieldChange = (fieldName: string, value: string) => {
    // Always update pendingChanges, even if value is the same as current
    setPendingChanges(prev => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const getDisplayValue = (fieldName: string): string => {
    // First check if there's a pending change from user input
    if (pendingChanges[fieldName]) {
      return pendingChanges[fieldName];
    }
    // Then check if there's a pending change request from server
    const changeRequest = getChangeRequestForField(fieldName);
    if (changeRequest) {
      return changeRequest.new_value;
    }
    return '';
  };

  const handleRejectChangeRequest = (requestId: number) => {
    // Immediately update UI to show rejected status
    setChangeRequests(prev => prev.map(cr => 
      cr.id === requestId 
        ? { ...cr, status: 'rejected' as const, reviewed_at: new Date().toISOString() }
        : cr
    ));
    
    // Mark for rejection (will be processed on Save)
    setPendingRejections(prev => {
      if (prev.includes(requestId)) return prev;
      return [...prev, requestId];
    });
    setPendingApprovals(prev => prev.filter(id => id !== requestId));
    message.info('Change request will be rejected when you click Save');
  };

  const getChangeRequestForField = (fieldName: string): CustomerChangeRequest | undefined => {
    return changeRequests.find(cr => cr.field_name === fieldName && cr.status === 'pending');
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleTypeChange = async (newType: 'potential' | 'active' | 'inactive') => {
    setCustomerType(newType);
    form.setFieldValue('type', newType);
    
    // If in edit mode, save immediately
    if (isEdit && id) {
      try {
        await customerService.update(parseInt(id, 10), { type: newType });
        message.success('Customer type updated');
      } catch {
        message.error('Failed to update customer type');
        // Revert on error
        const currentType = form.getFieldValue('type') || 'potential';
        setCustomerType(currentType);
      }
    }
  };

  const handleFileChange = (field: string, files: UploadFile[]) => {
    setReportingFiles(prev => ({
      ...prev,
      [field]: files,
    }));
  };

  return (
    <Spin spinning={loadingCustomer} size="large">
      <Card bordered={false} bodyStyle={{ padding: 0 }} style={{ boxShadow: 'none' }}>
      {isEdit && customerName && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          paddingBottom: 16,
          borderBottom: '1px solid #f0f0f0',
        }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff', fontSize: 28, fontWeight: 600 }}>
            {customerName}
          </Title>
          <Space>
            {!isEditing && canUpdate && (
              <Button onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            )}
            {canDelete && (
              <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                Delete
              </Button>
            )}
          </Space>
        </div>
      )}
      
      {!isEdit && (
        <Title level={3} style={{ marginBottom: 24 }}>
          New Customer
        </Title>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ enabled: true, type: 'potential' }}
        disabled={loadingCustomer}
        onValuesChange={(changedValues, allValues) => {
          // Update customer name when name field changes
          if (changedValues.name !== undefined) {
            setCustomerName(changedValues.name);
          }
          // Update customer type when type field changes
          if (changedValues.type !== undefined) {
            setCustomerType(changedValues.type);
          }
        }}
      >
        <Tabs 
          activeKey={activeTab}
          onChange={handleTabChange}
          tabBarExtraContent={
            isEdit ? (
          <Space size={8}>
            <div
              onClick={() => handleTypeChange('potential')}
              style={{
                padding: '4px 12px',
                borderRadius: '12px',
                cursor: 'pointer',
                backgroundColor: customerType === 'potential' ? '#FFF7E6' : '#f5f5f5',
                border: customerType === 'potential' ? '1px solid #FFA940' : '1px solid #d9d9d9',
                color: customerType === 'potential' ? '#FF8C00' : '#595959',
                fontWeight: customerType === 'potential' ? 500 : 400,
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                transition: 'all 0.2s',
              }}
            >
              <span style={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                backgroundColor: customerType === 'potential' ? '#FF8C00' : '#8c8c8c',
              }}></span>
              Potential
            </div>
            <div
              onClick={() => handleTypeChange('active')}
              style={{
                padding: '4px 12px',
                borderRadius: '12px',
                cursor: 'pointer',
                backgroundColor: customerType === 'active' ? '#E6F7FF' : '#f5f5f5',
                border: customerType === 'active' ? '1px solid #40A9FF' : '1px solid #d9d9d9',
                color: customerType === 'active' ? '#1890ff' : '#595959',
                fontWeight: customerType === 'active' ? 500 : 400,
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                transition: 'all 0.2s',
              }}
            >
              <span style={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                backgroundColor: customerType === 'active' ? '#1890ff' : '#8c8c8c',
              }}></span>
              Active
            </div>
            <div
              onClick={() => handleTypeChange('inactive')}
              style={{
                padding: '4px 12px',
                borderRadius: '12px',
                cursor: 'pointer',
                backgroundColor: customerType === 'inactive' ? '#FFF1F0' : '#f5f5f5',
                border: customerType === 'inactive' ? '1px solid #FF7875' : '1px solid #d9d9d9',
                color: customerType === 'inactive' ? '#ff4d4f' : '#595959',
                fontWeight: customerType === 'inactive' ? 500 : 400,
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                transition: 'all 0.2s',
              }}
            >
              <span style={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                backgroundColor: customerType === 'inactive' ? '#ff4d4f' : '#8c8c8c',
              }}></span>
              Inactive
            </div>
          </Space>
            ) : null
          }
        >
          <TabPane tab="Overview" key="overview">
            <OverviewTab
              form={form}
              isEdit={isEdit}
              isEditing={isEditing}
              banks={banks}
              getDisplayValue={getDisplayValue}
              handleFieldChange={handleFieldChange}
              getChangeRequestForField={getChangeRequestForField}
              handleApproveChangeRequest={handleApproveChangeRequest}
              handleRejectChangeRequest={handleRejectChangeRequest}
              handleApprovePendingChange={handleApprovePendingChange}
              handleRejectPendingChange={handleRejectPendingChange}
              reportingFiles={reportingFiles}
              onFileChange={handleFileChange}
              customerId={id ? parseInt(id, 10) : undefined}
            />
          </TabPane>

          <TabPane tab="Engagement" key="engagement">
            <EngagementTab
              form={form}
              staff={staff}
              teams={teams}
              isEditing={isEditing}
              reportingFiles={reportingFiles}
              onFileChange={handleFileChange}
              customerId={id ? parseInt(id, 10) : undefined}
              onEngagementDateChange={handleEngagementDateChange}
            />
          </TabPane>

          <TabPane tab="Business" key="business">
            <BusinessTab customerId={id ? parseInt(id, 10) : undefined} />
          </TabPane>

          <TabPane tab="Family" key="family">
            <FamilyTab
              isEdit={isEdit}
              isEditing={isEditing}
              isCustomerPortal={false}
              spouses={spouses}
              children={children}
              deletedFamilyMembers={deletedFamilyMembers}
              changeRequests={changeRequests.map(cr => ({
                field_name: cr.field_name,
                status: cr.status,
                old_value: cr.old_value || '',
                new_value: cr.new_value || '',
                id: cr.id,
              }))}
              addSpouse={addSpouse}
              removeSpouse={removeSpouse}
              addChild={addChild}
              removeChild={removeChild}
              handleFamilyTypeChange={handleFamilyTypeChange}
              getFamilyDisplayValue={getFamilyDisplayValue}
              handleFamilyFieldChange={handleFamilyFieldChange}
              handleApproveFamilyDelete={handleApproveFamilyDelete}
              handleRejectFamilyDelete={handleRejectFamilyDelete}
              handleApproveChangeRequest={handleApproveChangeRequest}
              handleRejectChangeRequest={handleRejectChangeRequest}
              customerId={id ? parseInt(id, 10) : undefined}
              onFamilyFileChange={(type, index, file) => {
                const fileKey = `${type}_${index}`;
                setFamilyFiles(prev => ({
                  ...prev,
                  [fileKey]: file ? [file] : [],
                }));
              }}
              familyFiles={familyFiles}
            />
          </TabPane>

          <TabPane tab="Assets" key="assets">
            <AssetsTab
              isEdit={isEdit}
              isEditing={isEditing}
              existingAssets={existingAssets}
              planningAssets={planningAssets}
              deletedAssets={deletedAssets}
              addExistingAsset={addExistingAsset}
              removeExistingAsset={removeExistingAsset}
              updateExistingAsset={updateExistingAsset}
              addPlanningAsset={addPlanningAsset}
              removePlanningAsset={removePlanningAsset}
              updatePlanningAsset={updatePlanningAsset}
              getAssetDisplayValue={getAssetDisplayValue}
              handleAssetFieldChange={handleAssetFieldChange}
              handleApproveAssetChange={handleApproveAssetChange}
              handleRejectAssetChange={handleRejectAssetChange}
              changeRequests={changeRequests}
              handleApproveAssetRequests={handleApproveAssetRequests}
              handleRejectAssetRequests={handleRejectAssetRequests}
              handleApproveAssetDelete={handleApproveAssetDelete}
              handleRejectAssetDelete={handleRejectAssetDelete}
              handleApproveChangeRequest={handleApproveChangeRequest}
              handleRejectChangeRequest={handleRejectChangeRequest}
              isCustomerPortal={false}
            />
          </TabPane>

          <TabPane tab="Timeline" key="timeline">
            <TimelineTab
              changeRequests={changeRequests}
            />
          </TabPane>
        </Tabs>
      </Form>

      <div style={{ 
        padding: '16px 24px', 
        borderTop: '1px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: 24,
        position: 'sticky',
        bottom: 0,
        backgroundColor: '#fff',
        zIndex: 10,
        
      }}>
        <Space>
          {isEditing && (
            <>
              <Button onClick={() => {
                if (isEdit && id) {
                  setIsEditing(false);
                  // Reload customer data to reset form
                  loadCustomer(parseInt(id, 10));
                } else {
                  // For create mode, navigate back
                  navigate('/customers');
                }
              }}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                onClick={() => {
                  form.submit();
                }} 
                loading={submitting}
              >
                {isEdit ? 'Save' : 'Create'}
              </Button>
            </>
          )}
          {!isEditing && isEdit && (
            <Button onClick={() => navigate('/customers')}>Cancel</Button>
          )}
        </Space>
      </div>
      </Card>
    </Spin>
  );
};
